<template>
  <div class="popover-wrapper">
    <Popover
      ref="popoverRef"
      position="top-center"
      color="unset"
      :popoverStyle="{ padding: '0px', top: '100px' }"
      :withOverlay="true"
    >
      <template v-slot:content='{ on }'>
        <Input 
          :value="displayedValue" 
          @click="inputClicked"
          :id="inputId"
          :type="isPrice ? 'text' : 'number'"
          v-on="on"
          :style="{ borderColor: isPrice && (!isValidInternal && internalValue !== previousValue) ? 'red' : '' }"
          @input="validateInput"
          :disabled="isDisabled"
          readonly
        />
      </template>

      <template #popoverContent>
        <div class="popover-wrapper__content">
          <label>{{ text }}</label>
          <Input 
            ref="popoverInput"
            v-if="!isPrice"
            :id="inputId"
            type="number"
            :value="internalValue"
            @input="validateInput"
            @blur="handleBlur"
            :style="{ borderColor: isPrice && (!isValidInternal &&  internalValue !== previousValue) ? 'red' : '' }"
          />
          <CurrencyInput 
            @focus="$event.target.select()"
            ref="popoverInput"
            v-else
            v-model="internalValue"
            color="unset"
            @input="validateInput"
            :id="inputId"
            @blur="handleBlur"
            :options="{ locale: 'tr-TR', currency: 'TRY', currencyDisplay: 'hidden', hideGroupingSeparatorOnFocus: false, hideNegligibleDecimalDigitsOnFocus: false, autoDecimalDigits: false, useGrouping: true, accountingSign: false, valueRange: { min: 0 } }"
            :style="{ borderColor: isPrice && (!isValidInternal &&  internalValue !== previousValue) ? 'red' : '', border: isPrice && (!isValidInternal &&  internalValue !== previousValue) ? '1px solid red' : '' }"
          />
          <Button 
            primary
            sm 
            :loading="isActionClick"
            :disabled="(isPrice && !isValidInternal) || !internalValue || isActionClick" 
            :class="{ 'disabled-button': (isPrice && !isValidInternal) || !internalValue }"
            @click="handleUpdate"
          >
            Güncelle
          </Button>
        </div>
      </template>
    </Popover>
  </div>
</template>

<script>
import { Popover, Input, Button } from '@n11/so-ui-components';
import CurrencyInput from './CurrencyInput'
import { useStore } from 'vuex'
import { watch } from 'vue'

export default {
  name: 'PopoverInput',
  components: { Popover, Input, Button, CurrencyInput },
  props: {
    inputId: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    validate: {
      type: Function,
      required: true
    },
    isValid: {
      type: Boolean,
      required: true
    },
    isPrice: {
      type: Boolean,
      required: false,
      default: false
    },
    format: {
      type: Function,
      required: false,
      default: value => value
    },
    isDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    isActionClick: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      internalValue: this.value,
      isValidInternal: this.isValid,
      popoverRef: null,
      isActionClick: this.isActionClick,
      previousValue: this.value,
      timeout: null
    };
  },
  computed: {
    displayedValue() {
      return this.format(this.internalValue);
    }
  },
  mounted() {
    const store = useStore();
    watch(
      () => store.getters["products/getIsProductUpdate"],
      (newValue) => {
        if (newValue && store.getters["products/getActivePriceAreaInput"] === this.inputId) {
          this.$refs.popoverRef?.popoverToogle();
        }
      },
      { immediate: true }
    )
  },
  watch: {
    isValid(newValidity) {
      this.isValidInternal = newValidity;
    }
  },
  methods: {
    validateInput(e) {
      const value = e.target.value;
      if (!this.isPrice && !/^\d+$/.test(value)) return;

      this.internalValue = value;
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.isValidInternal = this.validate(this.internalValue);
        this.$emit('input', this.internalValue);
        this.$emit('validity', this.isValidInternal);
      }, 50);
    },

    handleUpdate() {
      if (!this.isPrice && !/^\d+$/.test(this.internalValue)) {
        return;
      }
      
      const revert = () => {
        this.internalValue = this.previousValue;
        this.$refs.popoverInput?.setValue(this.previousValue);

        return false;
      }

      if (this.isPrice && !this.isValidInternal) {
        return revert()
      }

      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (this.validate(this.internalValue)) {
          this.$emit('update', this.internalValue, revert);
        }
      }, 50);
    },

    handleBlur(e) {
      const value = e.target.value;
      if (!this.isPrice && !/^\d+$/.test(value)) {
        this.internalValue = this.previousValue;
        this.$refs.popoverInput?.setValue(this.previousValue);
        return;
      }

      if (this.timeout) clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          if (this.isPrice && this.internalValue !== this.previousValue) {
            this.internalValue = this.previousValue;
            this.$refs.popoverInput?.setValue(this.internalValue);
          }
        }, 150);
    },

    inputClicked(e) {
      if (this.isPrice) {
        e.target.select();
      }
    },
  }
};

</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.popover-wrapper {
  :deep(.popover-text){
    margin-top: 100px !important;
    z-index: 9999 !important;
  }

  &__content {
    display: inline-flex;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    border-radius: 8px;
    border: 1px solid $colorN50;
    box-shadow: 0 0 0 3px $gray-32;
    background: $colorN00;

    label {
      font-size: 12px;
      font-weight: 700;
      line-height: 16px;
      color: $colorN80;
    }
  }
  .disabled-button {
    background-color: grey;
    cursor: not-allowed;
  }
}
</style>
